// Service-worker registration for the PWA shell. The 2026-05-20 salad
// incident surfaced the failure mode: with the previous `autoUpdate`
// config + `globPatterns` that precached `index.html`, every page open
// served the cached old shell instantly and only checked for a new SW
// in the background. Users opening the PWA *after* a deploy got the
// OLD bundle on that first session — the very session in which they'd
// then hit the new server behavior. The user-reported `app_version`
// stuck at a SHA two deploys behind because the cached HTML still
// referenced the old hashed JS bundle.
//
// Fix shape:
//   - `index.html` is no longer precached (see `vite.config.ts:workbox
//     .globPatterns`) — a runtimeCaching NetworkFirst rule handles it
//     instead, so the page request races the network first and falls
//     back to cache only when truly offline (or network <3 s slow).
//   - When the new SW finishes installing and finds a different
//     precache manifest from the active one, it fires `onNeedRefresh`;
//     we call `location.reload()` so the user picks up the fresh
//     shell + new hashed assets in the SAME session.
//
// Tested via `sw-register.test.ts` (asserts the reload fires on
// `onNeedRefresh` and not on `onOfflineReady`).

import { registerSW } from 'virtual:pwa-register';

export type SwLifecycleHandlers = {
  readonly onNeedRefresh: () => void;
  readonly onOfflineReady: () => void;
};

// Side-effect-free factory so the test suite can construct the
// handlers without invoking the real `registerSW`.
export function makeSwLifecycleHandlers(reload: () => void): SwLifecycleHandlers {
  return {
    onNeedRefresh() {
      // A new service worker has finished precaching and is ready to
      // take over. Because the Workbox config sets `skipWaiting: true`
      // + `clientsClaim: true`, the new SW already owns this client;
      // reloading just swaps the HTML to the fresh shell.
      reload();
    },
    onOfflineReady() {
      // First-install success — nothing to do. The hook exists so
      // future iterations could surface a toast ("Ready offline").
    },
  };
}

// Invoked once from `main.tsx`. Safe to call in non-browser environments
// (the `virtual:pwa-register` module is a no-op when the build has SW
// disabled, e.g. dev mode without `VITE_PLUGIN_PWA_DEV_OPTIONS_ENABLED`).
export function installServiceWorker(): void {
  const handlers = makeSwLifecycleHandlers(() => {
    globalThis.location.reload();
  });
  registerSW({
    immediate: true,
    onNeedRefresh: handlers.onNeedRefresh,
    onOfflineReady: handlers.onOfflineReady,
  });
}
